import { inject, Injectable } from '@angular/core';
import { NotificationEntity, UserEntity } from '@app/@core/entities';
import { NotificationType, SocketEvents } from '@app/@core/enums';
import { NotificationService } from '@app/@core/services';
import { SocketIoService } from '@app/@core/socket-io';
import { plainToInstance } from 'class-transformer';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { PermissionService } from '@auth';
import { GetUserId, isMobileView } from '@core/utils';
import { UseClub, UseNotification } from '@core/usecases';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class NotificationsComposition {
  notificationsSubject: BehaviorSubject<NotificationEntity[]> = new BehaviorSubject<NotificationEntity[]>([]);
  notifications$: Observable<NotificationEntity[]> = this.notificationsSubject.asObservable();
  closePopover$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  teamUsers: UserEntity[];
  useNotification = new UseNotification();
  private readonly _notificationService = inject(NotificationService);
  private readonly _socketService = inject(SocketIoService);
  private readonly _permissionService = inject(PermissionService);
  private readonly _useClub = new UseClub();
  private readonly _router: Router = inject(Router);

  private _notificationsMap: Record<string, NotificationEntity[]> = {
    [NotificationType.Group]: [],
    [NotificationType.News]: [],
    [NotificationType.FAQ]: [],
    [NotificationType.Instructor]: [],
    [NotificationType.Room]: [],
    [NotificationType.Event]: [],
    [NotificationType.Task]: [],
    [NotificationType.Survey]: [],
    [NotificationType.Course]: [],
    [NotificationType.Message]: [],
  };

  constructor() {
    this._subscribeToSocket();
  }

  get hasNotifications(): boolean {
    return Object.values(this._notificationsMap).some((notifications) => notifications.length > 0);
  }

  fetchNotifications() {
    // fetch team users for User images/avatar in notifications component
    this._useClub?.getClubMembers().then((users) => {
      this.teamUsers = users;
    });

    if (this._permissionService.isAdmin()) {
      this._fetchAdminNotificationData();
    } else if (!this._permissionService.isGuest()) {
      this._fetchUserNotificationData();
    }
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // --------------------------- Public Functions to be used in Notifications Component Only -----------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  /**
   * These below notification action functions and names are adapted from accept-deny service which are copied from Legacy code.
   */

  // ----------------------------------------------------------------------------------
  // ------------------------------------ GROUPS --------------------------------------
  // ----------------------------------------------------------------------------------

  public _joinGroup(groupId: number, notificationUserId: number) {
    console.log('joinGroup', groupId, notificationUserId);
    this.useNotification.joinGroup(groupId, notificationUserId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Group);
      },
    });
  }

  public _acceptGroup(groupId: number) {
    this.useNotification.acceptGroup(groupId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Group);
      },
    });
  }

  public _denyGroup(groupId: number, otherUserId: number) {
    this.useNotification.denyGroup(groupId, otherUserId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Group);
      },
    });
  }

  public _acceptUpdatedGroup(groupId: number) {
    this.useNotification.updateGroup(groupId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Group);
      },
    });
  }

  public _viewUpdatedGroup(groupId: number) {
    this.useNotification.viewGroup(groupId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Group);
        this.reloadCurrentRoute(!isMobileView() ? `/groups/${groupId}/details` : `/m/groups/${groupId}/details`);
      },
    });
  }

  public _viewGroup(groupId: number) {
    this.reloadCurrentRoute(!isMobileView() ? `/groups/${groupId}/details` : `/m/groups/${groupId}/details`);
  }

  public _approvedGroup(groupId: number) {
    this.useNotification.approvedGroup(groupId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Group);
      },
    });
  }

  public _viewAcceptedGroup(groupId: number) {
    this.useNotification.viewAcceptedGroup(groupId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Group);
        this.reloadCurrentRoute(!isMobileView() ? `/groups/${groupId}/details` : `/m/groups/${groupId}/details`);
        this.closePopover$.next(true);
      },
    });
  }

  public _readPublishedGroup(groupId: number) {
    this.useNotification.readPublishedGroup(groupId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Group);
        this.reloadCurrentRoute(!isMobileView() ? `/groups/${groupId}/details` : `/m/groups/${groupId}/details`);
      },
    });
  }

  public _unapproveDenyGroup(groupId: number, reason: any) {
    this.useNotification.unapproveDenyGroup(groupId, reason).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Group);
      },
    });
  }

  public _deleteGroup(groupId: number) {
    this.useNotification.deleteGroup(groupId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Group);
      },
    });
  }

  // ----------------------------------------------------------------------------------
  // ------------------------------------ NEWS ----------------------------------------
  // ----------------------------------------------------------------------------------

  public _acceptNews(newsId: number) {
    this.useNotification.acceptNews(newsId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.News);
      },
    });
  }

  public _viewNews(newsId: number) {
    this.reloadCurrentRoute(isMobileView() ? '/m/club-wall/news' : '/discover/news', { newsId });
  }

  public _acceptUpdateNews(newsId: number) {
    this.useNotification.acceptUpdateNews(newsId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.News);
      },
    });
  }

  public _denyNews(newsId: number, reason: any) {
    this.useNotification.denyNews(newsId, reason).subscribe({
      next: () => {
        this._emitNotification(NotificationType.News);
      },
    });
  }

  public _viewApprovePublishNewsByAdmin(newsId: number) {
    this.useNotification.viewApprovePublishNewsByAdmin(newsId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.News);
        this.reloadCurrentRoute(isMobileView() ? '/m/club-wall/news' : '/discover/news', { newsId });
      },
    });
  }

  public _viewApproveUpdateNewsByAdmin(newsId: number) {
    this.useNotification.viewApproveUpdateNewsByAdmin(newsId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.News);
        this.reloadCurrentRoute(isMobileView() ? '/m/club-wall/news' : '/discover/news', { newsId });
      },
    });
  }

  public _handleNewsApproval(notification: NotificationEntity) {
    this.useNotification.handleNewsApproval(notification.id).subscribe({
      next: () => {
        this._emitNotification(NotificationType.News);
      },
    });
  }

  public _handleNewsDisapproval(notification: NotificationEntity) {
    this.useNotification.handleNewsDisapproval(notification.id).subscribe({
      next: () => {
        this._emitNotification(NotificationType.News);
      },
    });
  }

  // ----------------------------------------------------------------------------------
  // ------------------------------------ EVENTS --------------------------------------
  // ----------------------------------------------------------------------------------

  public _approvedEvents(eventId: number) {
    this.useNotification.approvedEvents(eventId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Event);
      },
    });
  }

  public _approvedUpdateEvents(eventId: number) {
    this.useNotification.approvedUpdateEvents(eventId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Event);
      },
    });
  }

  public _viewDenyPublishEventByAdmin(eventId: number) {
    this.reloadCurrentRoute(isMobileView() ? `m/club-wall/events/${eventId}/details` : 'club-overview/events', { eventId });
  }

  public _unapprovedEvent(eventId: number, reason?: string) {
    this.useNotification.unapprovedEvent(eventId, reason).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Event);
      },
    });
  }

  public _denyEvent(eventId: number) {
    this.useNotification.denyEvent(eventId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Event);
      },
    });
  }

  public _acceptInvitedEvent(eventId: number, selectedEventDates: any) {
    this.useNotification.acceptInvitedEvent(eventId, selectedEventDates).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Event);
      },
    });
  }

  public _denyInvitedEvent(eventId: number) {
    this.useNotification.denyInvitedEvent(eventId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Event);
      },
    });
  }

  public _viewAcceptPublishEventByAdmin(eventId: number) {
    this.useNotification.viewApprovePublishEventByAdmin(eventId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Event);
        this.reloadCurrentRoute(isMobileView() ? `m/club-wall/events/${eventId}/details` : 'club-overview/events', { eventId });
      },
    });
  }

  public _viewApproveUpdateEventByAdmin(eventId: number) {
    this.useNotification.viewApproveUpdateEventByAdmin(eventId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Event);
        this.reloadCurrentRoute(isMobileView() ? `m/club-wall/events/${eventId}/details` : 'club-overview/events', { eventId });
      },
    });
  }

  public _viewUpdateEvent(eventId: number) {
    this.useNotification.viewUpdateEvent(eventId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Event);
        this.reloadCurrentRoute(isMobileView() ? `m/club-wall/events/${eventId}/details` : 'club-overview/events', { eventId });
      },
    });
  }

  // ----------------------------------------------------------------------------------
  // ------------------------------------ TASKS ---------------------------------------
  // ----------------------------------------------------------------------------------

  public _adminApprovedTasks(taskId: number) {
    this.useNotification.adminApprovedTasks(taskId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Task);
      },
    });
  }

  public _adminUnapprovedTasks(taskId: number, reason: any) {
    this.useNotification.adminUnapprovedTasks(taskId, reason).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Task);
      },
    });
  }

  public _adminApprovedUpdateTasks(taskId: number) {
    this.useNotification.adminApprovedUpdateTasks(taskId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Task);
      },
    });
  }

  public _acceptInvitedTask(taskId: number) {
    this.useNotification.acceptInvitedTask(taskId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Task);
        this.reloadCurrentRoute(isMobileView() ? '/m/tasks' : '/tasks', { taskId });
        this.closePopover$.next(true);
      },
    });
  }

  public _acceptInvitedSubTask(taskId: number) {
    this.useNotification.acceptInvitedSubTask(taskId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Task);
        this.reloadCurrentRoute(isMobileView() ? '/m/tasks' : '/tasks', { taskId });
        this.closePopover$.next(true);
      },
    });
  }

  public _viewUpdatedTask(taskId: number) {
    this.useNotification.viewUpdatedTask(taskId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Task);
        this.reloadCurrentRoute(isMobileView() ? '/m/tasks' : '/tasks', { taskId });
        this.closePopover$.next(true);
      },
    });
  }

  public _viewCompletedTask(taskId: number) {
    this.useNotification.viewCompletedTask(taskId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Task);
        this.reloadCurrentRoute(isMobileView() ? '/m/tasks' : '/tasks', { taskId });
        this.closePopover$.next(true);
      },
    });
  }

  public _viewAcceptPublishTaskByAdmin(taskId: number) {
    this.useNotification.viewAcceptPublishTaskByAdmin(taskId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Task);
        this.reloadCurrentRoute(isMobileView() ? '/m/tasks' : '/tasks', { taskId });
        this.closePopover$.next(true);
      },
    });
  }

  public _viewAcceptUpdatedTaskByAdmin(taskId: number) {
    this.useNotification.viewAcceptUpdatedTaskByAdmin(taskId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Task);
        this.reloadCurrentRoute(isMobileView() ? '/m/tasks' : '/tasks', { taskId });
        this.closePopover$.next(true);
      },
    });
  }

  public _viewDenyPublishTaskByAdmin(taskId: number) {
    this.reloadCurrentRoute(isMobileView() ? '/m/tasks' : '/tasks', { taskId });
    this.closePopover$.next(true);
  }

  // ----------------------------------------------------------------------------------
  // ---------------------------------- MESSAGES --------------------------------------
  // ----------------------------------------------------------------------------------

  public _acceptMessage(esdb_id: number) {
    this.useNotification.acceptMessage(esdb_id).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Message);
      },
    });
  }

  public _denyMessage(esdb_id: number) {
    this.useNotification.denyMessage(esdb_id).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Message);
      },
    });
  }

  // ----------------------------------------------------------------------------------
  // ------------------------------------ ROOMS ---------------------------------------
  // ----------------------------------------------------------------------------------

  public _approvedRooms(roomId: number) {
    this.useNotification.approvedRooms(roomId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Room);
      },
    });
  }

  public _approvedUpdateRooms(roomId: number) {
    this.useNotification.approvedUpdateRooms(roomId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Room);
      },
    });
  }

  public _unapprovedRooms(roomId: number, reason: any) {
    this.useNotification.unapprovedRooms(roomId, reason).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Room);
      },
    });
  }

  public _viewApprovePublishRoomByAdmin(roomId: number) {
    this.useNotification.viewApprovePublishRoomByAdmin(roomId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Room);
        this.reloadCurrentRoute(isMobileView() ? '/m/courses/rooms' : '/courses/rooms', { roomId });
      },
    });
  }

  public _viewApproveUpdateRoomByAdmin(roomId: number) {
    this.useNotification.viewApproveUpdateRoomByAdmin(roomId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Room);
        this.reloadCurrentRoute(isMobileView() ? '/m/courses/rooms' : '/courses/rooms', { roomId });
      },
    });
  }

  public _viewDenyPublishRoomByAdmin(roomId: number) {
    this.reloadCurrentRoute(isMobileView() ? '/m/courses/rooms' : '/courses/rooms', { roomId });
  }

  // ----------------------------------------------------------------------------------
  // --------------------------------- INSTRUCTORS ------------------------------------
  // ----------------------------------------------------------------------------------

  public _approvedInstructors(instructor_id: number) {
    this.useNotification.approvedInstructors(instructor_id).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Instructor);
      },
    });
  }

  public _approvedUpdateInstructors(instructor_id: number) {
    this.useNotification.approvedUpdateInstructors(instructor_id).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Instructor);
      },
    });
  }

  public _unapprovedInstuctors(instructor_id: number, reason: any) {
    this.useNotification.unapprovedInstuctors(instructor_id, reason).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Instructor);
      },
    });
  }

  public _viewApproveUpdateInstructorByAdmin(instructorId: number): void {
    this.useNotification.viewApproveUpdateInstructorByAdmin(instructorId, +GetUserId()).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Instructor);
        this.reloadCurrentRoute(isMobileView() ? '/m/courses/instructors' : '/courses/instructors', { instructorId });
      },
    });
  }

  public _viewDenyPublishInstructorByAdmin(instructorId: number): void {
    this.reloadCurrentRoute(isMobileView() ? '/m/courses/instructors' : '/courses/instructors', { instructorId });
  }

  public _viewApprovePublishInstructorByAdmin(instructorId: number): void {
    this.useNotification.viewApprovePublishInstructorByAdmin(instructorId, +GetUserId()).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Instructor);
        this.reloadCurrentRoute(isMobileView() ? '/m/courses/instructors' : '/courses/instructors', { instructorId });
      },
    });
  }

  // ----------------------------------------------------------------------------------
  // ------------------------------------- FAQS ---------------------------------------
  // ----------------------------------------------------------------------------------

  public _approvedFaqs(faqId: number) {
    this.useNotification.approvedFaqs(faqId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.FAQ);
      },
    });
  }

  public _approveUpdateFaqs(faqId: number) {
    this.useNotification.approveUpdateFaqs(faqId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.FAQ);
      },
    });
  }

  public _denyFaqs(faqId: number, reason: any) {
    this.useNotification.denyFaqs(faqId, reason).subscribe({
      next: () => {
        this._emitNotification(NotificationType.FAQ);
      },
    });
  }

  public _readApprovedFaqs(faqId: number) {
    this.useNotification.readApprovedFaqs(faqId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.FAQ);
        this.reloadCurrentRoute(isMobileView() ? 'm/club-tools/faq' : 'club-tools/faq', { faqId });
        this.closePopover$.next(true);
      },
    });
  }

  public _readUpdatedFaqs(faqId: number) {
    this.useNotification.readUpdatedFaqs(faqId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.FAQ);
        this.reloadCurrentRoute(isMobileView() ? 'm/club-tools/faq' : 'club-tools/faq', { faqId });
        this.closePopover$.next(true);
      },
    });
  }

  public _approvedFaqsCategory(faqId: number) {
    this.useNotification.approvedFaqsCategory(faqId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.FAQ);
      },
    });
  }

  public _unapprovedFaqsCategory(faqId: number) {
    this.useNotification.unapprovedFaqsCategory(faqId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.FAQ);
      },
    });
  }

  public _viewFaqs(faqId: number) {
    this.reloadCurrentRoute(isMobileView() ? 'm/club-tools/faq' : 'club-tools/faq', { faqId });
    this.closePopover$.next(true);
  }

  // ----------------------------------------------------------------------------------
  // ----------------------------------- SURVEYS --------------------------------------
  // ----------------------------------------------------------------------------------

  public _approvedSurvey(surveyId: number) {
    this.useNotification.approvedSurvey(surveyId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Survey);
      },
    });
  }

  public _unapprovedSurvey(surveyId: number, reason: any) {
    this.useNotification.unapprovedSurvey(surveyId, reason).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Survey);
      },
    });
  }

  public _approvedUpdatedSurvey(surveyId: number) {
    this.useNotification.approvedUpdatedSurvey(surveyId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Survey);
      },
    });
  }

  public _acceptInvitedSurvey(surveyId: number) {
    this.useNotification.acceptInvitedSurvey(surveyId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Survey);
      },
    });
    // The id is not passed as we just want to navigate user to survey list
    this.reloadCurrentRoute(isMobileView() ? '/m/club-tools/surveys' : '/club-tools/surveys');
    this.closePopover$.next(true);
  }

  public _viewAcceptPublishSurveyByAdmin(surveyId: number) {
    this.useNotification.viewAcceptPublishSurveyByAdmin(surveyId).subscribe();
    this.reloadCurrentRoute(isMobileView() ? '/m/club-tools/surveys' : '/club-tools/surveys', { surveyId });
    this.closePopover$.next(true);
  }

  public _viewPublishUpdateSurveyByAdmin(surveyId: number) {
    this.useNotification.viewPublishUpdateSurveyByAdmin(surveyId).subscribe();
    this.reloadCurrentRoute(isMobileView() ? '/m/club-tools/surveys' : '/club-tools/surveys', { surveyId });
    this.closePopover$.next(true);
  }

  public _denyInvitedSurvey(surveyId: number, reason: any): void {
    // For surveys we call the unapprovedSurvey endpoint with the reason.
    this.useNotification.unapprovedSurvey(surveyId, reason).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Survey);
      },
    });
  }

  public _viewDenyPublishSurveyByAdmin(surveyId: number): void {
    this.reloadCurrentRoute(isMobileView() ? '/m/club-tools/surveys' : '/club-tools/surveys', { surveyId });
  }

  // ----------------------------------------------------------------------------------
  // ------------------------------------ COURSES -------------------------------------
  // ----------------------------------------------------------------------------------

  public _acceptInvitedCourse(courseId: number) {
    this.useNotification.acceptInvitedCourse(courseId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Course);
      },
    });
  }

  public _denyInvitedCourse(courseId: number) {
    this.useNotification.denyInvitedCourse(courseId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Course);
      },
    });
  }

  public _acceptJoinUser(courseId: number, userId: number) {
    this.useNotification.acceptJoinUser(courseId, userId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Course);
      },
    });
  }

  public _denyJoinUser(courseId: number) {
    this.useNotification.denyJoinUser(courseId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Course);
      },
    });
  }

  public _approvedCourses(courseId: number) {
    this.useNotification.approvedCourses(courseId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Course);
      },
    });
  }

  public _approvedUpdateCourses(courseId: number) {
    this.useNotification.approvedUpdateCourses(courseId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Course);
      },
    });
  }

  public _unapprovedCourse(courseId: number, reason: string) {
    this.useNotification.unapprovedCourse(courseId, reason).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Course);
      },
    });
  }

  public _deleteUpdateCourse(courseId: number) {
    this.useNotification.deleteUpdateCourse(courseId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Course);
      },
    });
  }

  public _deleteCourse(courseId: number) {
    this.useNotification.deleteCourse(courseId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Course);
      },
    });
  }

  public _viewJoinedCourse(courseId: number) {
    this.useNotification.viewJoinedCourse(courseId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Course);
        this.reloadCurrentRoute(isMobileView() ? `/m/courses/${courseId}/details` : '/courses/', { courseId });
      },
    });
  }

  public _viewUpdateCourse(courseId: number) {
    this.useNotification.viewUpdateCourse(courseId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Course);
        this.reloadCurrentRoute(isMobileView() ? `/m/courses/${courseId}/details` : '/courses/', { courseId });
      },
    });
  }

  public _viewInternalCourse(courseId: number) {
    this.useNotification.viewInternalCourse(courseId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Course);
        this.reloadCurrentRoute(isMobileView() ? `/m/courses/${courseId}/details` : '/courses/', { courseId });
      },
    });
  }

  public _viewInternalUpdateCourse(courseId: number) {
    this.useNotification.viewInternalUpdateCourse(courseId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Course);
        this.reloadCurrentRoute(isMobileView() ? `/m/courses/${courseId}/details` : '/courses/', { courseId });
      },
    });
  }

  public _viewApprovePublishCourseByAdmin(courseId: number) {
    this.useNotification.viewApprovePublishCourseByAdmin(courseId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Course);
        this.reloadCurrentRoute(isMobileView() ? `/m/courses/${courseId}/details` : '/courses/', { courseId });
      },
    });
  }

  public _viewApproveUpdateCourseByAdmin(courseId: number) {
    this.useNotification.viewApproveUpdateCourseByAdmin(courseId).subscribe({
      next: () => {
        this._emitNotification(NotificationType.Course);
        this.reloadCurrentRoute(isMobileView() ? `/m/courses/${courseId}/details` : '/courses/', { courseId });
      },
    });
  }

  public _viewDenyPublishCourseByAdmin(courseId: number) {
    this.reloadCurrentRoute(isMobileView() ? `/m/courses/${courseId}/details` : '/courses/', { courseId });
  }

  public reloadCurrentRoute(path?: string, queryParams?: Record<string, any>) {
    const currentUrl = path || this._router.url;
    this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this._router.navigate([currentUrl], { queryParams });
      this.closePopover$.next(true);
    });
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------ Private Protected Functions ----------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  private _fetchAdminNotificationData() {
    Object.values(NotificationType).forEach((type) => {
      this._fetchNotificationData(type, true);
    });
  }

  private _fetchUserNotificationData() {
    Object.values(NotificationType).forEach((type) => {
      this._fetchNotificationData(type, false);
    });
  }

  private _fetchNotificationData(type: string, isAdmin: boolean) {
    const methodMap = {
      [NotificationType.Group]: isAdmin
        ? this._notificationService.getUnapprovedGroupNotification.bind(this._notificationService)
        : this._notificationService.getAllGroupsUsersNotifications.bind(this._notificationService),
      [NotificationType.News]: isAdmin
        ? this._notificationService.getUnapprovedNewsNotification.bind(this._notificationService)
        : this._notificationService.getAllNewsUsersNotifications.bind(this._notificationService),
      [NotificationType.FAQ]: isAdmin
        ? this._notificationService.getUnapprovedFaqNotification.bind(this._notificationService)
        : this._notificationService.getAllFAQsUsersNotifications.bind(this._notificationService),
      [NotificationType.Instructor]: isAdmin
        ? this._notificationService.getUnapprovedInstructorNotification.bind(this._notificationService)
        : this._notificationService.getAllInstructorsUsersNotifications.bind(this._notificationService),
      [NotificationType.Room]: isAdmin
        ? this._notificationService.getUnapprovedRoomsNotification.bind(this._notificationService)
        : this._notificationService.getAllRoomsUsersNotifications.bind(this._notificationService),
      [NotificationType.Event]: isAdmin
        ? this._notificationService.getUnapprovedEventNotification.bind(this._notificationService)
        : this._notificationService.getAllEventsUsersNotifications.bind(this._notificationService),
      [NotificationType.Task]: isAdmin
        ? this._notificationService.getUnapprovedTasksNotifications.bind(this._notificationService)
        : this._notificationService.getAllTasksUsersNotifications.bind(this._notificationService),
      [NotificationType.Survey]: isAdmin
        ? this._notificationService.getUnapprovedSurveysNotifications.bind(this._notificationService)
        : this._notificationService.getAllSurveysUsersNotifications.bind(this._notificationService),
      [NotificationType.Course]: isAdmin
        ? this._notificationService.getUnapprovedCoursesNotifications.bind(this._notificationService)
        : this._notificationService.getAllCoursesUsersNotifications.bind(this._notificationService),
      // [NotificationType.Message]: this._notificationService.getAllMessageNotifications.bind(this._notificationService),
    };

    const method = methodMap[type];
    if (method) {
      method()
        .pipe(
          map((respData: any[]) => {
            respData.forEach((data: any) => (data.section = type));
            return respData.map((data: any) => plainToInstance(NotificationEntity, data));
          }),
        )
        .subscribe({
          next: (notifications: NotificationEntity[]) => {
            this._notificationsMap[type] = this._filterNotifications(notifications);
            this._mergeNotifications();
          },
        });
    }
  }

  private _filterNotifications(notifications: NotificationEntity[]): NotificationEntity[] {
    return notifications;
  }

  private _mergeNotifications() {
    const merged = Object.values(this._notificationsMap).flat();
    this.notificationsSubject.next(merged);
  }

  private _subscribeToSocket() {
    this._socketService.fromEvent(SocketEvents.GetNotification).subscribe({
      next: (data: NotificationType) => {
        if (Object.values(NotificationType).includes(data)) {
          const isAdmin = this._permissionService.isAdmin();
          this._fetchNotificationData(data, isAdmin);
        } else {
          console.warn('Unknown notification type:', data);
        }
      },
    });
  }

  private _emitNotification(notificationType: NotificationType) {
    if (notificationType) {
      this._socketService.emit(SocketEvents.SendNotification, notificationType);
    }
  }
}
